import { createContext, useContext, type ComponentType, type PropsWithChildren, type JSX } from 'react';
import type { ChatComponentProps } from './chatbot.types';
import type { ChatbotEventBridge } from './chatbot-event-bridge';

/** Configuration for avatar rendering per role. */
export type AvatarConfig = {
  /** 'image' renders an <img>, 'initials' renders text, 'custom' renders a component. */
  type: 'image' | 'initials' | 'custom';
  /** For 'image': src URL. For 'initials': the text to show. */
  value?: string;
  /** For 'image': alt text. */
  alt?: string;
  /** CSS class for the avatar wrapper (e.g. role-specific styling). */
  className?: string;
  /** For 'custom': a React component to render. */
  component?: ComponentType;
};

export type ChatbotConfig = {
  /** Prefix used for localStorage keys (e.g. `'woz-case'` → `'woz-case-chat'`). */
  storagePrefix?: string;
  /** Maps componentId → React component for rendering inside bubbles. */
  componentRegistry?: Record<string, ComponentType<ChatComponentProps>>;
  /** Avatar rendering configuration per role. */
  avatar?: Record<string, AvatarConfig>;
  /** Display labels per role (e.g. { assistant: 'Assistent' }). */
  roleLabels?: Record<string, string>;
  /** Placeholder text for the input field. */
  inputPlaceholder?: string;
  /** Placeholder text when input is disabled. */
  inputDisabledPlaceholder?: string;
  /** Send button label. */
  sendButtonLabel?: string;
  /** Send button aria-label. */
  sendButtonAriaLabel?: string;
  /** Event bridge for chat ↔ host communication. */
  eventBridge?: ChatbotEventBridge;
};

const defaultConfig: ChatbotConfig = {
  componentRegistry: {},
};

const ChatbotContext = createContext<ChatbotConfig>(defaultConfig);

export const ChatbotProvider = ({ config, children }: PropsWithChildren<{ config: ChatbotConfig }>): JSX.Element => {
  return <ChatbotContext.Provider value={config}>{children}</ChatbotContext.Provider>;
};

export const useChatbotConfig = (): ChatbotConfig => {
  return useContext(ChatbotContext);
};

/** Convenience hook — returns the event bridge from the chatbot config (if provided). */
export const useChatbotEventBridge = (): ChatbotEventBridge | undefined => {
  const { eventBridge } = useChatbotConfig();
  return eventBridge;
};
